import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Platform,
} from 'react-native';
import Svg, { Path, Rect, Circle } from 'react-native-svg';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as DocumentPicker from 'expo-document-picker';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { FONTS } from '../../constants/config';
import { organizationsApi } from '../../services/apiService';
import { queryKeys } from '../../providers/QueryProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import ConfirmModal from '../../components/ConfirmModal';
import { getTextAlign } from '../../utils/rtl';
import { getFullMediaUrl } from '../../utils/media';
import { MainStackParamList } from '../../navigation/MainNavigator';

interface PendingFile {
  uri: string;
  name: string;
  type: string;
  size?: number;
}

// Mirrors the backend's validate_banner_image_size (10MB).
const MAX_BANNER_BYTES = 10 * 1024 * 1024;

// The backend explains a rejected upload ({error}, {detail} or DRF field
// errors) — surface that instead of a generic "couldn't save".
function serverErrorMessage(err: any): string | undefined {
  const data = err?.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  const first = Object.values(data)[0];
  if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  return undefined;
}

async function pickImage(): Promise<PendingFile | null> {
  const result = await DocumentPicker.getDocumentAsync({
    type: ['image/png', 'image/jpeg', 'image/webp', 'image/gif'],
    multiple: false,
  });
  if (result.canceled || !result.assets?.[0]) return null;
  const asset = result.assets[0];
  return {
    uri: asset.uri,
    name: asset.name ?? 'image.png',
    type: asset.mimeType ?? 'image/png',
    size: asset.size,
  };
}

function fileToBlobLike(file: PendingFile): any {
  if (Platform.OS === 'web') return undefined;
  return { uri: file.uri, name: file.name, type: file.type };
}

async function appendFileToFormData(form: FormData, key: string, file: PendingFile) {
  if (Platform.OS === 'web') {
    const res = await fetch(file.uri);
    const blob = await res.blob();
    form.append(key, blob, file.name);
  } else {
    form.append(key, fileToBlobLike(file));
  }
}

const PlaceholderIcon = ({ color }: { color: string }) => (
  <Svg width={28} height={28} viewBox="0 0 24 24" fill="none">
    <Rect x={3} y={5} width={18} height={14} rx={2} stroke={color} strokeWidth={1.7} />
    <Circle cx={9} cy={11} r={2} stroke={color} strokeWidth={1.7} />
    <Path
      d="M21 17l-5-5-9 9"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Default banner template (Fujairah skyline) shown in the preview when no
// custom background is set — mirrors what the live banner falls back to.
const DEFAULT_SKYLINE = require('../../../assets/images/banner-default-skyline.png');

const PAGE_BG = '#fcfcfc';
const CARD_BG = tokens.colors.semantic.general.background;
const INNER_BG = tokens.colors.semantic.general.accent;
const BORDER = tokens.colors.semantic.general.border;
const FOREGROUND = tokens.colors.semantic.general.foreground;
const MUTED = tokens.colors.semantic.general['muted foreground'];
const REQUIRED_RED = '#d62728';

export default function EditOrganizationBannerScreen() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { isRTL, language } = useLanguage();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  // The department whose banner is edited: the one the organisation page was
  // showing (a multi-department user's choice), else the user's own.
  const route = useRoute<RouteProp<MainStackParamList, 'EditOrganizationBanner'>>();
  const orgId = (route.params?.organizationId ??
    (user?.profile as any)?.organization) as number | undefined;

  const { data: organization, isLoading } = useQuery({
    queryKey: orgId ? queryKeys.organizations.detail(orgId) : ['organizations', 'detail', 'unknown'],
    queryFn: () => organizationsApi.get(orgId as number),
    enabled: Boolean(orgId),
  });

  const [description, setDescription] = useState<string>('');
  const [pendingBg, setPendingBg] = useState<PendingFile | null>(null);
  const [pendingLogo, setPendingLogo] = useState<PendingFile | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    if (organization) {
      const initial = isRTL
        ? organization.banner_tagline_ar ?? ''
        : organization.banner_tagline_en ?? '';
      setDescription(initial);
    }
  }, [organization?.id, isRTL]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('No organization');

      if (pendingBg) {
        const form = new FormData();
        await appendFileToFormData(form, 'file', pendingBg);
        await organizationsApi.uploadBanner(orgId, form);
      }
      if (pendingLogo) {
        const form = new FormData();
        await appendFileToFormData(form, 'logo', pendingLogo);
        await organizationsApi.uploadLogo(orgId, form);
      }
      // Save only the locale matching the active UI language; preserve the other.
      const taglinePatch = isRTL
        ? { banner_tagline_ar: description }
        : { banner_tagline_en: description };
      await organizationsApi.updateBannerTaglines(orgId, taglinePatch);
    },
    onSuccess: async () => {
      if (orgId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.detail(orgId),
          refetchType: 'all',
        });
      }
      showSuccess(t('myOrganization.banner.edit.toastSaved'));
      navigation.goBack();
    },
    onError: (err) => {
      showError(serverErrorMessage(err) ?? t('myOrganization.banner.edit.toastError'));
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('No organization');
      await organizationsApi.resetBanner(orgId);
    },
    onSuccess: async () => {
      if (orgId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.detail(orgId),
          refetchType: 'all',
        });
      }
      showSuccess(t('myOrganization.banner.edit.toastReset'));
      setShowResetModal(false);
      navigation.goBack();
    },
    onError: (err) => {
      showError(serverErrorMessage(err) ?? t('myOrganization.banner.edit.toastError'));
      setShowResetModal(false);
    },
  });

  const handlePickBg = useCallback(async () => {
    const file = await pickImage();
    if (!file) return;
    if (file.size && file.size > MAX_BANNER_BYTES) {
      showError(
        t('myOrganization.banner.edit.bgTooLarge', {
          size: (file.size / (1024 * 1024)).toFixed(1),
        }),
      );
      return;
    }
    setPendingBg(file);
  }, [showError, t]);

  const handlePickLogo = useCallback(async () => {
    const file = await pickImage();
    if (file) setPendingLogo(file);
  }, []);

  const textAlignValue = getTextAlign(isRTL);
  const chooseFileLabel = language === 'ar' ? 'اختر ملف' : 'Choose file';
  const noFileLabel = language === 'ar' ? 'لم يتم اختيار ملف' : 'No file chosen';

  const logoPreviewUri =
    pendingLogo?.uri ?? getFullMediaUrl(organization?.logo ?? null);
  const bgPreviewUri =
    pendingBg?.uri ?? getFullMediaUrl(organization?.banner_background ?? null);
  const logoFileName =
    (pendingLogo?.name && pendingLogo.name.trim()) ||
    (pendingLogo ? 'image' : noFileLabel);
  const bgFileName =
    (pendingBg?.name && pendingBg.name.trim()) ||
    (pendingBg ? 'image' : noFileLabel);

  if (isLoading || !organization) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loading}><ActivityIndicator size="large" /></View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView contentContainerStyle={styles.scroll}>
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.myOrganization') },
            { label: t('myOrganization.banner.edit.title') },
          ]}
        />

        <View style={styles.outerCard}>
          <Text style={[styles.outerDescription, { textAlign: textAlignValue }]}>
            {t('myOrganization.banner.edit.title')}
          </Text>

          <View style={styles.innerCard}>
            <View style={styles.innerHeader}>
              <Text style={[styles.innerHeaderText, { textAlign: textAlignValue }]}>
                {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
              </Text>
            </View>

            {/* Description */}
            <View style={styles.field}>
              <Text style={[styles.label, { textAlign: textAlignValue }]}>
                {language === 'ar' ? 'الوصف' : 'Description'}
              </Text>
              <TextInput
                value={description}
                onChangeText={setDescription}
                maxLength={200}
                multiline
                numberOfLines={3}
                style={[
                  styles.textarea,
                  { textAlign: textAlignValue, writingDirection: isRTL ? 'rtl' : 'ltr' },
                ]}
                placeholder={
                  language === 'ar'
                    ? 'مثال: نعمل على تعزيز التميز المؤسسي…'
                    : 'e.g. Driving institutional excellence…'
                }
                placeholderTextColor={MUTED}
              />
            </View>

            {/* Logo file input */}
            <View style={styles.field}>
              <Text style={[styles.label, { textAlign: textAlignValue }]}>
                {language === 'ar' ? 'الشعار' : 'Logo'}
              </Text>
              <TouchableOpacity
                onPress={handlePickLogo}
                activeOpacity={0.8}
                style={styles.logoThumb}
              >
                {logoPreviewUri ? (
                  <Image
                    source={{ uri: logoPreviewUri }}
                    style={styles.logoThumbImage}
                    resizeMode="contain"
                  />
                ) : (
                  <View style={styles.thumbEmpty}>
                    <PlaceholderIcon color={MUTED} />
                  </View>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handlePickLogo}
                activeOpacity={0.7}
                style={styles.fileInputRow}
              >
                <View style={styles.chooseFileButton} pointerEvents="none">
                  <Text style={styles.chooseFileButtonText}>{chooseFileLabel}</Text>
                </View>
                <Text
                  style={[styles.fileName, { textAlign: textAlignValue }]}
                  numberOfLines={1}
                >
                  {logoFileName}
                </Text>
              </TouchableOpacity>
              <Text style={[styles.hint, { textAlign: textAlignValue }]}>
                {t('myOrganization.banner.edit.fieldLogoHint')}
              </Text>
            </View>

            {/* Banner background file input */}
            <View style={styles.field}>
              <Text style={[styles.label, { textAlign: textAlignValue }]}>
                {language === 'ar' ? 'صورة الخلفية' : 'Background image'}
              </Text>
              <TouchableOpacity
                onPress={handlePickBg}
                activeOpacity={0.8}
                style={styles.bgThumb}
              >
                <Image
                  source={bgPreviewUri ? { uri: bgPreviewUri } : DEFAULT_SKYLINE}
                  style={StyleSheet.absoluteFillObject}
                  resizeMode="cover"
                />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handlePickBg}
                activeOpacity={0.7}
                style={styles.fileInputRow}
              >
                <View style={styles.chooseFileButton} pointerEvents="none">
                  <Text style={styles.chooseFileButtonText}>{chooseFileLabel}</Text>
                </View>
                <Text
                  style={[styles.fileName, { textAlign: textAlignValue }]}
                  numberOfLines={1}
                >
                  {bgFileName}
                </Text>
              </TouchableOpacity>
              <Text style={[styles.hint, { textAlign: textAlignValue }]}>
                {t('myOrganization.banner.edit.fieldBackgroundHint')}
              </Text>
            </View>
          </View>
        </View>

        <View style={[styles.footer, { flexDirection: isRTL ? 'row' : 'row-reverse' }]}>
          <Button
            variant="destructive"
            size="md"
            onPress={() => setShowResetModal(true)}
          >
            {t('myOrganization.banner.edit.reset')}
          </Button>
          <View style={{ flex: 1 }} />
          <Button variant="secondary" size="md" onPress={() => navigation.goBack()}>
            {t('myOrganization.banner.edit.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onPress={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
          >
            {t('myOrganization.banner.edit.save')}
          </Button>
        </View>
      </ScrollView>

      <ConfirmModal
        visible={showResetModal}
        type="danger"
        title={t('myOrganization.banner.edit.confirmResetTitle')}
        message={t('myOrganization.banner.edit.confirmResetBody')}
        confirmText={t('myOrganization.banner.edit.reset')}
        cancelText={t('myOrganization.banner.edit.cancel')}
        onConfirm={() => resetMutation.mutate()}
        onCancel={() => setShowResetModal(false)}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scroll: {
    padding: 20,
    paddingBottom: 80,
    backgroundColor: PAGE_BG,
    minHeight: '100%' as any,
  },
  outerCard: {
    backgroundColor: CARD_BG,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    padding: 20,
    gap: 20,
    marginTop: 20,
    shadowColor: 'rgba(0,0,0,0.1)',
    shadowOpacity: 1,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 1.5,
    elevation: 1,
  },
  outerDescription: {
    fontFamily: FONTS.medium,
    fontSize: 16,
    color: FOREGROUND,
    lineHeight: 24,
  },
  innerCard: {
    backgroundColor: INNER_BG,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
    padding: 24,
    gap: 24,
  },
  innerHeader: {
    width: '100%',
  },
  innerHeaderText: {
    fontFamily: FONTS.bold,
    fontSize: 16,
    color: FOREGROUND,
    lineHeight: 24,
  },
  field: {
    width: '100%',
    gap: 6,
  },
  label: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    lineHeight: 20,
  },
  required: {
    color: REQUIRED_RED,
    fontFamily: FONTS.regular,
  },
  textarea: {
    width: '100%',
    minHeight: 60,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    shadowColor: 'rgba(0,0,0,0.05)',
    shadowOpacity: 1,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 1,
    elevation: 0.5,
  },
  logoThumb: {
    width: 96,
    height: 96,
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: BORDER,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginBottom: 4,
  },
  logoThumbImage: {
    width: '80%',
    height: '80%',
  },
  bgThumb: {
    width: '100%',
    aspectRatio: 3.2,
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: BORDER,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginBottom: 4,
  },
  thumbEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileInputRow: {
    width: '100%',
    minHeight: 36,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    shadowColor: 'rgba(0,0,0,0.05)',
    shadowOpacity: 1,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 1,
    elevation: 0.5,
  },
  chooseFileButton: {
    minHeight: 24,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    backgroundColor: tokens.colors.semantic.general.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chooseFileButtonText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: tokens.colors.semantic.general['secondary foreground'],
    lineHeight: 14,
  },
  fileName: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: FOREGROUND,
    lineHeight: 20,
  },
  hint: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: MUTED,
    lineHeight: 16,
  },
  footer: {
    gap: 8,
    marginTop: 16,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  loading: { padding: 80, alignItems: 'center' },
});
